import request from '@/utils/request';
import * as types from '@/api/repo/types';
import { reqCatch, reqCatchV2, type catchRt } from '@/utils/catch';
import type { CommonWithPageReqType } from '@/utils/types';
import { ISSUE_PR_API_PREFIX } from '@/constant/index';
import { AC_NEW_URL } from '@/constant/keys';
// 新增里程碑
export function addRepoMilestones(data: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `${ISSUE_PR_API_PREFIX}/api/v1/projects/${data.repoId}/milestones`,
    method: 'post',
    data
  });
}
// 编辑里程碑
export function editRepoMilestones(data: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `${ISSUE_PR_API_PREFIX}/api/v1/projects/${data.repoId}/milestones/${data.id}`,
    method: 'put',
    data
  });
}
// 删除里程碑
export function deletedRepoMilestones(data: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `${ISSUE_PR_API_PREFIX}/api/v1/projects/${data.repoId}/milestones/${data.id}`,
    method: 'delete'
  });
}
// 获取里程碑列表
export function getRepoMilestones(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `${ISSUE_PR_API_PREFIX}/api/v1/projects/${params.repoId}/milestones`,
    method: 'get',
    params
  });
}
// 获取里程碑详情
export function getRepoMileStoneDetail(params: {
  project_id: string;
  milestone_id: string;
}): Promise<types.commonRepoResType> {
  const { project_id, milestone_id } = params;
  return request({
    url: `${ISSUE_PR_API_PREFIX}/api/v1/projects/${project_id}/milestones/${milestone_id}`,
    method: 'get'
  });
}
// 获取项目贡献者
export function getRepoContributors(
  params: types.commonRepoReqType
): catchRt<CommonWithPageReqType<types.ContributorReqType[]>> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/${params.repoId}/repository/contributors`,
      method: 'get',
      params
    })
  );
}

// 获取项目的模型分析和使用的模型列表
export function getModelCardData(params: { repoId: string; }) {
  return request(
    {
      url: `/api/v2/ai-hub/nauth/internal/projects/${params.repoId}/repository/repo_card`,
      method: 'get'
    }
  );
}

// 获取用户在项目中的权限
export function getRepoRole(params: { repoId: string; }) {
  return request(
    {
      url: `/api/v2/projects/${params.repoId}/role`,
      method: 'get'
    },
    {
      customError: true
    }
  );
}

// 获取issue负责人列表
export function getIssueAssignee(params: {
  repoId: string;
  issue_iid?: string;
  page?: number;
  per_page?: number;
  search?: string;
}): Promise<any> {
  const { repoId, ...param } = params;
  return request(
    {
      url: `${ISSUE_PR_API_PREFIX}/api/v1/issue/${repoId}/issues/assignee_candidates`,
      method: 'get',
      params: Object.assign({ page: 1, per_page: 10 }, param)
    },
    {
      customError: true
    }
  );
}

/** 获取 issue 创建者/负责人 列表，只用于 repo issue 列表筛选 */
export function getIssueAssigneeList(params: {
  repoId: string;
  page?: number;
  per_page?: number;
  query?: string;
  current_user_at_top?: boolean;
  user_type: 'assignee' | 'author';
}): Promise<any> {
  const { repoId, ...param } = params;
  return request(
    {
      url: `${ISSUE_PR_API_PREFIX}/api/v1/issue/${repoId}/issues/users`,
      method: 'get',
      params: Object.assign({ page: 1, per_page: 10 }, param)
    },
    {
      customError: true
    }
  );
}

// 获取项目成员
export function getRepoMember(params: {
  repoId: string;
  page?: number;
  per_page?: number;
  query?: string;
}): Promise<any> {
  const { repoId, ...param } = params;
  return request(
    {
      url: `/api/v2/ac/projects/${repoId}/project-members`,
      method: 'get',
      params: Object.assign({ page: 1, per_page: 10 }, param)
    },
    {
      customError: true
    }
  );
}
// 根据配置获取项目成员以及组织成员
export function getMemberBySetting(params: {
  repoId: string;
  page?: number;
  per_page?: number;
  query?: string;
}): Promise<any> {
  const { repoId, ...param } = params;
  return request(
    {
      url: `/api/v2/ac/projects/${repoId}/mentions/suggestions/members`,
      method: 'get',
      params: Object.assign({ page: 1, per_page: 10 }, param)
    },
    {
      customError: true
    }
  );
}
// 获取项目成员角色信息
export function getRepoMembers(params: {
  projectId: string | number;
  page?: number;
  per_page?: number;
  query?: string;
  onlyMembers?: boolean;
}): Promise<any> {
  const { projectId } = params;
  return request(
    {
      url: `/api/v2/ac/projects/${projectId}/project-members`,
      method: 'get',
      params: Object.assign({ page: 1, per_page: 10 }, params)
    },
    {
      customError: true
    }
  );
}

// 获取安全漏洞派生项目成员信息
export function getSecureRepoMembers(params: {
  projectId: string | number;
  page?: number;
  per_page?: number;
  query?: string;
  onlyMembers?: boolean;
}): Promise<any> {
  const { projectId } = params;
  return request(
    {
      url: `/api/v2/ac/projects/${projectId}/secure-issue-project-members`,
      method: 'get',
      params: Object.assign({ page: 1, per_page: 10 }, params)
    }
  );
}

/** 获取项目下等待邀请的成员列表 */
export function getRepoInviteMember(params: { repoId: string; page?: Number; per_page?: number; }): Promise<any> {
  const { repoId, ...pager } = params;
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/projects/${repoId}/members/invite`,
      params: Object.assign({ page: 1, per_page: 100 }, pager)
    })
  );
}

/** 项目设置项目成员中邀请成员 */
export function toInviteUser(data: { repoId: string; conf: Record<string, any>; }) {
  const { repoId, conf } = data;
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/invitation/${repoId}/invite`,
      method: 'post',
      data: {
        ...conf,
        source_type: '1' // 0 组织 1 项目 2是企业
      }
    })
  );
}

/** fork 仓&& 私仓，项目设置-项目成员中，通过源仓角色，来邀请成员 */
export function toInviteForkRepoUsersByRole(data: { repoId: string; conf: Record<string, any>; }) {
  const { repoId, conf } = data;
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/fork-projects/${repoId}/inviteRoleUser`,
      method: 'post',
      params: {
        ...conf
      }
    })
  );
}

/** 项目设置撤销邀请 */
export function retractRepoInvite(data: { repoId: string; username: string; }) {
  const { repoId, username } = data;
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/invitation/${repoId}/1/members/${username}/revoke`,
      method: 'delete'
    })
  );
}

/** 项目设置退出项目 */
export function exitRepo(data: { repoId: string;[x: string]: any; }) {
  const { repoId } = data;
  // console.log(data)
  return reqCatch(() =>
    request({
      url: `/api/v1/projects/${repoId}/leave`,
      method: 'delete'
    })
  );
}

/** 项目设置移除用户 */
export function removeRepoUser(data: { repoId: string; username: string; }) {
  const { repoId, username } = data;
  return reqCatch(() =>
    request({
      url: `/api/v1/projects/${repoId}/members/${username}`,
      method: 'delete'
    })
  );
}
/** 项目设置移除用户（新） */
export function removeRepoMember(data: { projectId: string; username: string;[x: string]: any; }) {
  const { projectId, username } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/ac/projects/${projectId}/project-members/${username}`,
      method: 'delete'
    })
  );
}

/** 组织成员设置权限 */
export function setRepoUserLevel(data: {
  repoId: string;
  username: string;
  access_level: string | number;
  role_uuid: string;
}) {
  const { repoId, username, access_level, role_uuid } = data;
  return reqCatch(() =>
    request({
      url: `/api/v1/projects/${repoId}/members/${username}`,
      method: 'put',
      data: { access_level, role_uuid }
    })
  );
}
// 更新成员角色信息
export function updateRepoUserCharacter(data: { projectId: string | number; memberId: string; role_id: string; }) {
  const { projectId, memberId, role_id } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/ac/projects/${projectId}/project-members/${memberId}`,
      method: 'put',
      params: {
        roleId: role_id
      }
      // data: { access_level }
    })
  );
}

// 获取GitIgnore列表
export function getGitIgnoreList(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/templates/gitignore_keys`,
    method: 'get',
    params
  });
}
// 获取LICENSE列表
export function getLicenseList(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/templates/license_keys`,
    method: 'get',
    params
  });
}
// 文件详情页-文件搜索，加载repo所有文件
export function getRepoFileList(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/repository/file_list`,
    method: 'get',
    params
  }, { customError: true });
}
// 获取镜像项目曾用名列表
export function getMirrorRepoOldNames(projectId: number): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${projectId}/extend/githubRepoChangePath`,
    method: 'get'
  }, { customError: true });
}
export function getWikiFileList(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/repository/file_list`,
    method: 'get',
    params
  }, { customError: true });
}
// 获取项目的通知状态
export function getRepoNotice(params: types.commonRepoReqType): catchRt<types.RepoNoticeReqType> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/${params.repoId}/watch`,
      method: 'get',
      params: { project_id: params.repoId }
    })
  );
}
// 修改项目的通知状态
export function updateRepoNotice(data: types.commonRepoReqType): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/${data.repoId}/watch`,
      method: 'put',
      data
    })
  );
}
// 查询项目状态
export function getRepoStatus(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/fork_status`,
    method: 'get',
    params
  });
}
// 查询项目导入状态
export function getRepoImportStatus(params: types.commonRepoReqType): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/${params.repoId}/import_status`,
      method: 'get',
      params
    })
  );
}

// 查询基于模板新建项目的状态
export function getRepoStatusByTpl(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/generate_status`,
    method: 'get',
    params
  });
}

/** wiki列表 */
export function getWikiList(params: types.WikiListReqType): catchRt<CommonWithPageReqType<types.WikiListResType[]>> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/wiki/list`,
      method: 'get',
      params
    })
  );
}
/** wiki创建 */
export function createdWiki(data: types.RepoWikiReqType): catchRt<string> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/wiki/create`,
      method: 'post',
      data
    })
  );
}
/** wiki编辑 */
export function updateWiki(data: types.RepoWikiReqType): catchRt<string> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/wiki/update`,
      method: 'put',
      data
    })
  );
}
/** wiki删除 */
export function deleteWiki(data: { repo_path: string; file_path: string; }): catchRt<number> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/wiki/delete`,
      method: 'delete',
      data
    })
  );
}
/** wiki详情 */
export function getWikiDetail(params: { repo_path: string; file_path: string; }): catchRt<types.WikiFileResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/wiki/detail`,
      method: 'get',
      params
    })
  );
}
/** wiki导航 */
export function wikiPages(data: { repo_path: string; }): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v1/wiki/pages`,
    method: 'post',
    data
  });
}
/** wiki的commit历史 */
export function wikiCommitHistory(
  params: types.RepoWikiHistoryReqType
): catchRt<CommonWithPageReqType<types.WikiHistoryResType[]>> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/wiki/commit_histroy`,
      method: 'get',
      params
    })
  );
}
/** 获取仓库下载压缩包 */
export function getArchives(params: types.archiveType): Promise<types.archiveType> {
  return request({
    url: `/api/v4/projects/${params.repoId}/repository/archive`,
    method: 'get',
    params
  });
}
// 项目迁移
export function repoMigrate(data: types.commonRepoReqType): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/mirror/v1/project/myGithub`,
      method: 'post',
      data
    })
  );
}
// 迁移批量导入
export function migrateImport(data: types.commonRepoReqType): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/mirror/v1/project/import`,
      method: 'post',
      data
    })
  );
}
// 上次的批量导入
export function recentMigrate(params: types.commonRepoReqType): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/mirror/v1/project/myLastGithub`,
      method: 'get',
      params
    })
  );
}

/** 创建项目成员邀请链接 **/
export function createRepoMemberLink(data: { repoId: string; } & types.MemberInviteLinkProps) {
  const { repoId, ...config } = data;
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/project/${repoId}/invite/link`,
      method: 'post',
      data: config
    })
  );
}
/** 删除项目成员邀请链接 */
export function deleteMemberInviteLink(repoId: string, inviteId: string) {
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/project/${repoId}/${inviteId}/invite/link`,
      method: 'delete'
    })
  );
}
/** 查询项目成员邀请链接列表 */
export function getMemberInviteLinks(params: { repoId: string; page?: number; per_page?: number, source_type?: string}) {
  const { repoId, ...pager } = params;
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/${repoId}/invite/link`,
      params: Object.assign({ page: 1, per_page: 10 }, pager)
    })
  );
}
/** 修改项目成员邀请链接 **/
export function updateRepoMemberLink(data: { repoId: string; inviteId: string; } & types.MemberInviteLinkProps) {
  const { repoId, inviteId, ...config } = data;
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/project/${repoId}/${inviteId}/invite/link`,
      method: 'put',
      data: config
    })
  );
}
/** 接受项目邀请链接 */
export function acceptRepoInvite(invite_code: string) {
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/${invite_code}/invite/accept`
    })
  );
}
/** 查看项目邀请链接待接受成员列表 */
export function getAcceptRepoMember(params: { repoId: string; page?: number; per_page?: number, source_type?: string}) {
  const { repoId, ...pager } = params;
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/${repoId}/invite/accept/member`,
      params: Object.assign({ page: 1, per_page: 10 }, pager)
    })
  );
}
/** 审核链接访问的成员 */
export function checkRepoLinkMember(data: {
  repoId: string;
  id: number;
  access_level: string;
  member_expires_at?: string;
  audit: '0' | '2';
}) {
  const { repoId, id, ...conf } = data;
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/project/${repoId}/${id}/invite/audit`,
      method: 'put',
      data: conf
    })
  );
}
/** 删除链接访问的成员 */
export function removeRepoLinkMember(repoId: string, id: number) {
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/project/${repoId}/${id}/invite/audit`,
      method: 'delete'
    })
  );
}
/** 新增仓库镜像 */
export function createRepoMirror(data: { url: string; repoId: string, force: boolean; }) {
  const { repoId, ...conf } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/repo_remote_mirror`,
      method: 'post',
      data: conf
    })
  );
}
/** 获取仓库镜象 */
export function getRepoMirrorInfo(repoId: string) {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/repo_remote_mirror`
    })
  );
}
/** 删除仓库信息 */
export function delRepoMirrorInfo(repoId: string) {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/repo_remote_mirror`,
      method: 'delete'
    })
  );
}
/** 修改仓库镜像 */
export function updateMirrorInfo(data: { repoId: string; mirroring_enabled: boolean; force: boolean; }) {
  const { repoId, ...conf } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/repo_remote_mirror`,
      method: 'put',
      data: conf
    })
  );
}

/** 立即同步仓库镜像 */
export function syncRepoMirror(data: { repoId: string; }) {
  const { repoId, ...conf } = data;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/sync_remote_mirror`,
      method: 'post',
      data: conf
    })
  );
}
/** 邀请链接查询是否被邀请过 */
export function checkInvited(invite_code: string) {
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/${invite_code}/invite/accept/before`
    })
  );
}
/** 查询是否已被邀请 */
export function checkInvitedStatus(source_id: string, username: string) {
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/${source_id}/invite/accept/byUserName`,
      params: {
        username
      }
    })
  );
}

/** 查询是否已被邀请 */
export function checkInvitedEnterprisesStatus(enterprise_id: string, username: string) {
  return reqCatch(() =>
    request({
      url: `api/v2/enterprises/${enterprise_id}/enterprise-members/${username}`
    })
  );
}

/** 获取项目首页的topic数据 */
export function getRepoTopic(repoId: string) {
  return reqCatch(() =>
    request({
      url: `/api/v2/topics/project/${repoId}`
    })
  );
}
/** 更新topic列表 */
export function updateRepoTopic(repoId: string, topics: string[]) {
  return reqCatch(() =>
    request({
      url: `/api/v2/topics/project/${repoId}`,
      method: 'put',
      data: topics
    })
  );
}
/** 搜索topic */
export function searchRepoTopic(search: string) {
  return reqCatch(() =>
    request({
      url: `/api/v2/topics/search`,
      params: {
        search,
        page: 1,
        per_page: 100
      }
    })
  );
}
// 获取近28天star数量
export function getRepoStarsChart(projectId: number): Promise<any> {
  return request({
    url: `/api/mirror/v1/project/starDailyCount`,
    method: 'get',
    params: {
      projectId
    }
  });
}
// 获取项目贡献者
export function getRepoSearch(params: types.commonRepoReqType): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/v1/projects/search/page`,
      method: 'get',
      params
    })
  );
}

// 获取topic详情
export function getTopicDetail(topicId: string): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/topics/${topicId}`,
      method: 'get'
    })
  );
}

export function getTopicDetailByName(topicName: string): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/topics/byName/${topicName}`,
      method: 'get'
    })
  );
}

// 根据关键字搜索topic
export function getTopicSearch(params: types.commonRepoReqType): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/topics/search`,
      method: 'get',
      params
    })
  );
}

// 根据topic与语言过滤项目分页列表
export function getTopicRepos(params: types.commonRepoReqType): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/topics/${params.id}/projects`,
      method: 'get',
      params
    })
  );
}

// 当前用户star当前topic
export function starTopic(topicId: string): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/topics/${topicId}/user/star`,
      method: 'put'
    })
  );
}

// 当前用户star当前topic
export function unstarTopic(topicId: string): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/topics/${topicId}/user/unStar`,
      method: 'put'
    })
  );
}

// 本地部署设置
export function deploySetting(repoId: string, data: object): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/common/setting`,
      method: 'put',
      data
    })
  );
}

// 获取项目下载克隆数据
export function getRepoClone(repoId: string): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${repoId}/repository/download_statistics`,
      method: 'get'
    })
  );
}

// 获取分支差异数对比
export function getBranchDiff(project_id: string, branch: string): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${project_id}/repository/diverged`,
      method: 'get',
      params: {
        branch: branch
      }
    })
  );
}

// 判断代码是否存在冲突
export function getIsConflict(params: types.commonRepoReqType): Promise<any> {
  const { project_id, branch_name, other_project_id, other_branch_name } = params;
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${project_id}/branches/${branch_name}/exist_conflict/${other_project_id}/${other_branch_name}`,
      method: 'get'
    })
  );
}

// 同步分支代码
export function syncBranchCode(project_id: string, branch: string): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${project_id}/sync_repo`,
      method: 'put',
      data: {
        branch: branch
      }
    })
  );
}

// 拉取全部分支更新
export function syncBranchCodeAll(project_id: string): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${project_id}/sync_repo`,
      method: 'put',
      data: {
        branch: ''
      }
    })
  );
}

// 获取可选择的cla列表
export function getClaList(params: types.commonRepoReqType): catchRt<types.commonRepoResType> {
  return reqCatch(() =>
    request({
      url: `/api/v2/cla/projectClaList`,
      method: 'get',
      params
    })
  );
}

// 判断是否可同步
export function isEnableSyncCode(project_id: string, branch: string): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${project_id}/sync_repo`,
      method: 'get',
      params: {
        branch: branch
      }
    })
  );
}

// 获取MR相关的用户列表签署cla的情况
export async function getClaSignUser(repo_id: number, merge_request_iid: string): Promise<any> {
  // TODO:后端需要支持path直接调用
  // const repoStore = repoInfoStore();
  // await repoStore.updateSourceInfo();
  const curProjectId = repo_id; // || repoStore.repoInfo.id;

  return reqCatch(() =>
    request({
      url: `/api/v2/cla/${curProjectId}/merge_requests/${merge_request_iid}/cla_sign_users`,
      method: 'get'
    })
  );
}

// 仓库设置清理仓库
export function clearRepoSetting(project_id: string): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${project_id}/housekeeping`,
      method: 'post',
      data: {}
    })
  );
}

// 获取项目列表
export function getRepoList(params: object): Promise<any> {
  return request({
    url: `/api/v2/projects`,
    method: 'get',
    params
  });
}

// 从pr设置中获取审查及测试人信息
export function getPrSetting(data: types.commonRepoReqType): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${data.repoId}/pr_review/setting`,
      method: 'get'
    })
  );
}
// 预览
export function getDiscussionPreview(data: types.commonRepoReqType): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v1/discussion/preview`,
      method: 'post',
      data
    }, {
      customError: true
    })
  );
}

// 设置pr审查及测试人信息
export function setPrSetting(data: types.commonRepoReqType): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${data.repoId}/pr_review/setting`,
      method: 'put',
      data: data.params
    })
  );
}

// 获取设置中可选的审查人列表
export function getPrApproval(data: types.commonRepoReqType): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${data.repoId}/merge_requests/approval_user`,
      method: 'get',
      params: data.params
    })
  );
}

// 获取设置可选的审查人分页接口
export function getPrApprovalPage(data: types.commonRepoReqType): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/projects/${data.repoId}/merge_requests/approval_user/page`,
      method: 'get',
      params: data.params
    })
  );
}

// 获取原始评论数据
export const getOriginalComment = (params: types.DiscussionCommentProps): Promise<any> => {
  return reqCatch(() =>
    request({
      url: `/api/v1/discussion/${params.type}/${params.discussionId}`,
      method: 'get'
    })
  );
};

// 切换模式
export const changeMode = (params: types.modeParams): Promise<any> => {
  return reqCatch(() =>
    request({
      url: `/api/v2/ac/projects/${params.projectId}/transition`,
      method: 'put',
      data: {
        source_mgnt_mode: params.sourceMgntMode,
        target_mgnt_mode: params.targetMgntMode
      }
    })
  );
};

// 获取仓库权限模式
export const getRepoMode = async (projectId: string | number): Promise<any> => {
  // TODO:后端需要支持path直接调用
  // const repoStore = repoInfoStore();
  // await repoStore.updateSourceInfo();
  const curProjectId = projectId; // || repoStore.repoInfo.id;

  return reqCatch(() =>
    request({
      url: `/api/v2/ac/projects/${curProjectId}`,
      method: 'get'
    })
  );
};

// 获取仓库指定自定义角色及对应权限点
export function getProjectCustomRolesList(projectId: string | number, roleId: string) {
  return request({
    url: `/api/v2/ac/projects/${projectId}/custom-roles/${roleId}`,
    method: 'get'
  });
}

// 编辑仓库指定自定义角色及对应权限点
export function editProjectCustomRolesList(param: { projectId: string | number; roleId: string; data: any; }) {
  return request({
    url: `/api/v2/ac/projects/${param.projectId}/custom-roles/${param.roleId}`,
    method: 'put',
    data: param.data
  });
}
// 获取看板数据
export function relatedKanban(data: {
  path_with_namespace: string;
  source_type: 1 | 2; // 1 issues; 2 pr": 0
  iid: number; // issues iid、pr iid
}): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v1/kanban/issues_kanban_list`,
      method: 'post',
      data
    })
  );
}

// 获取组织下的看板
export function fetchKanbanList(params: {
  project_id: string; // 项目path
  search: string; // length <= 30
}): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v1/kanban/project/${params.project_id}/project_kanban_list`,
      method: 'get',
      params
    })
  );
}

// 添加issue 或 pr 到看板item
export function addKanbanItemByIssueOrPr(data: {
  kanban_id: string;
  project_id: string;
  status: string; // 状态保存的id(todo、in、done)
  source_reqs: {
    source_type: string; // 1、issues 2、pr 3、draft
    source_id: string;
  };
}): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v1/kanban/${data.kanban_id}/kanban_item/add_item_of_issues_pr`,
      method: 'post',
      data
    })
  );
}

// 添加issue 或 pr 到看板item
export function deleteKanbanItemByIssueOrPr(data: { kanban_id: string; kanban_item_ids: number[]; }): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v1/kanban/${data.kanban_id}/kanban_item`,
      method: 'delete',
      data
    })
  );
}

// 搜索聚合接口
export function getSearchResult(params: {}): Promise<any> {
  return reqCatch(() => request({
    url: `/api/v1/search/nauth/query`,
    method: 'get',
    params
  }));
};

// 搜索聚合计数接口
export function getSearchResultCount(params: {}): Promise<any> {
  return reqCatch(() => request({
    url: `/api/v1/search/nauth/query/count`,
    method: 'get',
    params
  }));
};

// 搜索图表查询
export function queryGraph(params: {}): Promise<any> {
  return reqCatch(() => request({
    url: `/api/v1/search/nauth/query/recommend/graph`,
    method: 'get',
    params
  }));
};

// 获取规则集
export function getRules(data: { project_id: string | number; }): Promise<any> {
  return reqCatch(() => request({
    url: `api/v2/projects/${data.project_id}/getRuleset`,
    method: 'get'
    // params
  }));
};

// 获取 tags 选项
export function getTags(params: { hot: string; }): Promise<any> {
  return reqCatch(() => request({
    url: `/aihub/api/v1/space/setting/labels`,
    method: 'get',
    params
  }));
};

// 获取 license、library、language、hardware 选项
export function getOptions(params: { code: string; }): Promise<any> {
  return reqCatch(() => request({
    url: `/aihub/api/v1/space_meta/get_code/${params.code}`,
    method: 'get'
    // params
  }));
};

// 获取 pipeline_tags 选项
export function getPipelineTags(): Promise<any> {
  return reqCatch(() => request({
    url: `/aihub/api/v1/space_meta/get_tags`,
    method: 'get'
  }));
};
/**
 *  同步push镜像
 */
export function syncRemoteMirrors(data: { project_id: string, remote_mirror_id: number, force: true; }): catchRt<types.RemoteMirror> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/${data.project_id}/push_remote_mirrors/${data.remote_mirror_id}`,
      method: 'post',
      data
    })
  );
}

/**
 *  添加push镜像 Add push remote mirror
 *  force:true 目前使用强推
 */
export function pushRemoteMirrors(data: { project_id: string, url: string, force: true; }): catchRt<types.RemoteMirrorResp> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/${data.project_id}/push_remote_mirrors`,
      method: 'post',
      data
    })
  );
}

/**
 *  获取push镜像地址列表 Get a list of push remote mirrors
*/
export function getRemoteMirrors(params: { project_id: string; }): catchRt<types.RemoteMirror[]> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/${params.project_id}/push_remote_mirrors`,
      method: 'get',
      params
    })
  );
}

/**
 *  删除push镜像
 */
export function deleteRemoteMirror(params: { project_id: string, remote_mirror_id: number; }): catchRt<unknown> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/${params.project_id}/push_remote_mirrors/${params.remote_mirror_id}`,
      method: 'delete',
      params
    })
  );
}

/**
 *  更新push镜像
 */
export function updateRemoteMirror(params: { project_id: string, remote_mirror_id: number, force: true; }): catchRt<unknown> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/${params.project_id}/push_remote_mirrors/${params.remote_mirror_id}`,
      method: 'put',
      params
    })
  );
}

/** 获取push镜像详情 */
export function getRemoteMirror(params: { project_id: string, remote_mirror_id: number; }): catchRt<types.RemoteMirror> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/${params.project_id}/push_remote_mirrors/${params.remote_mirror_id}`,
      method: 'get',
      params
    })
  );
}

/** 获取cangjie在线模板 */
export function getCangjieTemplate() {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/templates/cangjie/experience`,
      method: 'get'
    })
  );
}

/** 获取cangjie在线模板列表 */
export function getCangjieTemplateList() {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/templates/cangjie/experience/list`,
      method: 'get'
    })
  );
}
/** 送地址获取对应标题 **/
export function byProjectIid(data: any): Promise<any> {
  return request({
    url: `/issuepr/api/v1/issues/simpleInfo/byProjectIid`,
    method: 'post',
    data
  });
}

/** 首页项目推荐 算法接口 */
export function openSourceProject(params: any): Promise<any> {
  return request({
    url: `/api/v2/search/nauth/home/recommendProject`,
    method: 'get',
    params
  });
}

/* 批量解密别名 */
// 入参为 密文 用户username 和 企业id
export function batchDecryptAlias(data: { enterprise_id: string, encrypted_nameList: Array<{ username: string, enterprise_name: string }> }): Promise<any> {
  return request({
    url: `/api/v2/enterprises/${data.enterprise_id}/enterprise-members/userEnterpriseNameParse`,
    method: 'post',
    data: data.encrypted_nameList
  });
}

// 今日热门
export function getIndexHotProject(params: any): Promise<any> {
  return request({
    url: `/api/v1/search/nauth/home/indexHotProject`,
    method: 'get',
    params
  });
}

// star飙升
export function getIndexHotStarProject(params: any): Promise<any> {
  return request({
    url: `/api/v1/search/nauth/home/indexHotStarProject`,
    method: 'get',
    params
  });
}
// 下载热度
export function getIndexHotDownloadProject(params: any): Promise<any> {
  return request({
    url: `/api/v1/search/nauth/home/indexHotDlProject`,
    method: 'get',
    params
  });
}

export function getSearchCommonConfig(params?: any): Promise<any> {
  return request({
    url: `/api/v1/search/common/config`,
    method: 'get',
    params
  });
}
// 模型top
export function getModelTop(params: any): Promise<any> {
  return request({
    url: `/api/v1/search/common/config/model-top`,
    method: 'get',
    params
  });
}

// 举报接口
export function resourceReport(data: any): Promise<any> {
  return request({
    url: `/api/v2/resource-report`,
    method: 'post',
    data
  });
}

// 是否为 2025 年度项目/组织
export function getIs2025AnnualProject(params: {id: string, type: 'project' | 'group'}): Promise<any> {
  return request({
    url: `/api/v1/search/v2/gstar/tag`,
    method: 'get',
    params
  });
}

// 获取项目的osscompass数据
export function getOssCompassData(project_id: string): Promise<any> {
  return request({
    url: `/api/v2/oss-compass/project/${project_id}/metric-model/graph`,
    method: 'get'
  });
}
